import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Flight, Accommodation, DeductibleExpense, PerDiem } from '../context/RefundReportContext';
import { formatNumber } from '../utils/numberFormat';

interface ExportData {
  tripName: string;
  tripDates?: string;
  tripReason?: string;
  flights: Flight[];
  accommodations: Accommodation[];
  deductibleExpenses: DeductibleExpense[];
  perDiems: PerDiem[];
  currency: string;
  businessName?: string;
  businessLogo?: string;
}

interface TotalsData {
  flightTotal: number;
  accommodationTotal: number;
  deductibleTotal: number;
  perDiemTotal: number;
  grandTotal: number;
  totalCost: number;
}

class ReportExportService {
  /**
   * Export refund report to Excel format (single sheet)
   */
  exportToExcel(data: ExportData, totals: TotalsData): void {
    const workbook = XLSX.utils.book_new();

    // Build all data in a single sheet
    const allData: any[] = [
      // Header
      ['Travel Expense Refund Report'],
      ['Trip:', data.tripName],
      ['Dates:', data.tripDates || 'N/A'],
      ['Currency:', data.currency],
      [''],

      // Summary
      ['SUMMARY'],
      ['Category', 'Total'],
      ['Flights', totals.flightTotal],
      ['Accommodations', totals.accommodationTotal],
      ['Other Expenses', totals.deductibleTotal],
      ['Per Diem', totals.perDiemTotal],
      [''],
      ['Grand Total', totals.grandTotal],
      [''],
      [''],
    ];

    // Flights section
    if (data.flights.length > 0) {
      allData.push(
        ['FLIGHTS'],
        ['Flight', 'Date', 'Class', 'Cost', 'Currency', `Amount (${data.currency})`, 'Payment Method', 'Destination'],
        ...data.flights.map(f => [
          f.notes ? `${f.flight} * ${f.notes}` : f.flight,
          f.date,
          f.class,
          f.cost,
          f.currency,
          f.convertedAmount,
          f.paymentMethod,
          f.destinationCountry,
        ]),
        ['']
      );
    }

    // Accommodations section
    if (data.accommodations.length > 0) {
      allData.push(
        ['ACCOMMODATIONS'],
        ['Name', 'City', 'Date', 'Nights', 'Cost/Night', 'Currency', `Amount (${data.currency})`, 'Payment Method'],
        ...data.accommodations.map(a => [
          a.notes ? `${a.description} * ${a.notes}` : a.description,
          a.city,
          a.date,
          a.nights,
          a.costPerNight,
          a.currency,
          a.convertedAmount,
          a.paymentMethod,
        ]),
        ['']
      );
    }

    // Other expenses section
    if (data.deductibleExpenses.length > 0) {
      allData.push(
        ['OTHER EXPENSES'],
        ['Description', 'Category', 'Date', 'Amount', 'Currency', `Amount (${data.currency})`, 'Payment Method'],
        ...data.deductibleExpenses.map(e => [
          e.notes ? `${e.description} * ${e.notes}` : e.description,
          e.category,
          e.date,
          e.amount,
          e.currency,
          e.convertedAmount,
          e.paymentMethod,
        ]),
        ['']
      );
    }

    // Per diem section
    if (data.perDiems.length > 0) {
      allData.push(
        ['PER DIEM'],
        ['Country', 'Type', 'Days', 'Daily Rate (USD)', 'Total (USD)', 'Total (ILS)', 'Exchange Rate', 'Calculation Date'],
        ...data.perDiems.map(p => [
          p.country,
          p.type,
          p.days,
          p.daily_rate_usd || p.dailyAmountUSD,
          p.total_usd,
          p.total_ils,
          p.exchange_rate_usd_to_ils,
          p.calculation_date,
        ]),
        ['']
      );
    }

    // Final Summary Section (at bottom)
    allData.push(
      [''],
      ['FINAL SUMMARY'],
      ['Total Cost', totals.totalCost],
      ['Total Refund', totals.grandTotal],
    );

    // Create single sheet
    const sheet = XLSX.utils.aoa_to_sheet(allData);

    // Set column widths for better readability
    sheet['!cols'] = [
      { wch: 25 }, // Column A
      { wch: 15 }, // Column B
      { wch: 12 }, // Column C
      { wch: 12 }, // Column D
      { wch: 10 }, // Column E
      { wch: 15 }, // Column F
      { wch: 15 }, // Column G
      { wch: 15 }, // Column H
    ];

    XLSX.utils.book_append_sheet(workbook, sheet, 'Refund Report');

    // Generate filename with trip name and date
    const filename = `${data.tripName.replace(/[^a-z0-9]/gi, '_')}_Refund_Report_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file
    XLSX.writeFile(workbook, filename);
  }

  /**
   * Export refund report to PDF format
   */
  async exportToPDF(data: ExportData, totals: TotalsData): Promise<void> {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title on left
    doc.setFontSize(18);
    doc.text('Travel Expenses Report', 14, yPos);

    // Business logo in top right, aligned with title
    if (data.businessLogo) {
      try {
        // Load image as data URL
        const img = await this.loadImage(data.businessLogo);
        const logoSize = 20; // Logo height/width in mm
        const logoX = pageWidth - logoSize - 14; // 14mm margin from right
        doc.addImage(img, 'PNG', logoX, yPos - 5, logoSize, logoSize);
      } catch (error) {
        console.error('Failed to load business logo:', error);
        // Continue without logo
      }
    } else if (data.businessName) {
      // Only show business name if no logo
      doc.setFontSize(10);
      const businessNameX = pageWidth - 14; // Right margin
      doc.text(data.businessName, businessNameX, yPos, { align: 'right' });
    }

    yPos += 10;

    // Trip name and description on same line with comma
    doc.setFontSize(14);
    const tripInfo = data.tripReason
      ? `${data.tripName}, ${data.tripReason}`
      : data.tripName;
    doc.text(tripInfo, 14, yPos);
    yPos += 8;

    // Dates line
    doc.setFontSize(11);
    if (data.tripDates) {
      doc.text(`Dates: ${data.tripDates}`, 14, yPos);
      yPos += 6;
    }

    // Add 1 line spacing between header and content
    yPos += 6;

    // Flights table
    if (data.flights.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Flights', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Flight', 'Date', 'Class', 'Cost', 'Currency', `Amount (${data.currency})`, 'Payment']],
        body: data.flights.map(f => [
          f.notes ? `${f.flight} * ${f.notes}` : f.flight,
          f.date,
          f.class,
          formatNumber(f.cost),
          f.currency,
          formatNumber(f.convertedAmount),
          f.paymentMethod,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 73, 94] },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Accommodations table
    if (data.accommodations.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Accommodations', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Name', 'City', 'Date', 'Nights', 'Cost/Night', 'Currency', `Amount (${data.currency})`, 'Payment']],
        body: data.accommodations.map(a => [
          a.notes ? `${a.description} * ${a.notes}` : a.description,
          a.city,
          a.date,
          a.nights,
          formatNumber(a.costPerNight),
          a.currency,
          formatNumber(a.convertedAmount),
          a.paymentMethod,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 73, 94] },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Other expenses table
    if (data.deductibleExpenses.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Other Expenses', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Category', 'Date', 'Amount', 'Currency', `Amount (${data.currency})`, 'Payment']],
        body: data.deductibleExpenses.map(e => [
          e.notes ? `${e.description} * ${e.notes}` : e.description,
          e.category,
          e.date,
          formatNumber(e.amount),
          e.currency,
          formatNumber(e.convertedAmount),
          e.paymentMethod,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 73, 94] },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Per diem table
    if (data.perDiems.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Per Diem Allowances', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Country', 'Type', 'Days', 'Daily Rate (USD)', 'Total (ILS)']],
        body: data.perDiems.map(p => [
          p.country,
          p.type,
          p.days,
          formatNumber(p.daily_rate_usd || p.dailyAmountUSD),
          formatNumber(p.total_ils || 0),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 73, 94] },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Refund Summary table
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Add 2 lines spacing before Summary
    yPos += 12;

    doc.setFontSize(14);
    doc.text('Summary', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      body: [
        ['Total Cost', `${formatNumber(totals.totalCost)} ILS`],
        ['Total Refund to traveler', `${formatNumber(totals.grandTotal)} ILS`],
      ],
      theme: 'striped',
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' }
      },
    });

    // Generate filename
    const filename = `${data.tripName.replace(/[^a-z0-9]/gi, '_')}_Refund_Report_${new Date().toISOString().split('T')[0]}.pdf`;

    // Save PDF
    doc.save(filename);
  }

  /**
   * Load an image from a URL and convert to base64 data URL
   */
  private loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0);

        try {
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  private formatCurrency(amount: number, currency: string): string {
    return `${formatNumber(amount)} ${currency}`;
  }
}

export const reportExportService = new ReportExportService();
